// ============================================
// Journey Config Firestore Service
//
// Persists configurable journey step labels to
// Firestore so admins can rename steps without
// code changes. Loads saved labels on app init.
//
// Document: journey_config/step_labels
// ============================================

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { C_JOURNEY_CONFIG } from '../../constants/collections';
import type { JourneyStepLabels } from '../../config/journeyConfig';
import {
  DEFAULT_JOURNEY_CONFIG,
  getJourneyConfig,
  setJourneyConfig,
} from '../../config/journeyConfig';

const DOC_STEP_LABELS = 'step_labels';

interface StepLabelsDoc {
  labels: JourneyStepLabels;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Load saved step labels from Firestore and apply them
 * to the active journey config. Safe to call on app init —
 * falls back to defaults if no saved config exists.
 */
export async function loadJourneyStepLabels(): Promise<void> {
  try {
    const snap = await getDoc(doc(db, C_JOURNEY_CONFIG, DOC_STEP_LABELS));
    if (!snap.exists()) return;

    const data = snap.data() as StepLabelsDoc;
    if (!data.labels) return;

    const current = getJourneyConfig();
    setJourneyConfig({
      ...current,
      stepLabels: {
        ...current.stepLabels,
        ...data.labels,
      },
    });
  } catch (err) {
    // Non-fatal — defaults remain active
    console.warn('[journeyConfig] Failed to load saved step labels:', err);
  }
}

/**
 * Save custom step labels to Firestore.
 * Merges with defaults so partial updates are safe.
 */
export async function saveJourneyStepLabels(
  labels: JourneyStepLabels,
  updatedBy: string,
): Promise<void> {
  await setDoc(doc(db, C_JOURNEY_CONFIG, DOC_STEP_LABELS), {
    labels,
    updatedAt: new Date().toISOString(),
    updatedBy,
    _updatedAt: Timestamp.now(),
  });

  // Apply to active config immediately
  const current = getJourneyConfig();
  setJourneyConfig({
    ...current,
    stepLabels: { ...current.stepLabels, ...labels },
  });
}

/**
 * Reset step labels to defaults (removes Firestore override).
 */
export async function resetJourneyStepLabels(updatedBy: string): Promise<void> {
  const defaults = DEFAULT_JOURNEY_CONFIG.stepLabels;
  await saveJourneyStepLabels(defaults, updatedBy);
}

/**
 * Get the current saved labels (or defaults if none saved).
 */
export async function getSavedStepLabels(): Promise<JourneyStepLabels> {
  try {
    const snap = await getDoc(doc(db, C_JOURNEY_CONFIG, DOC_STEP_LABELS));
    if (snap.exists()) {
      const data = snap.data() as StepLabelsDoc;
      if (data.labels) return { ...DEFAULT_JOURNEY_CONFIG.stepLabels, ...data.labels };
    }
  } catch {
    // fall through to defaults
  }
  return { ...DEFAULT_JOURNEY_CONFIG.stepLabels };
}
